import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import "../styles/Register.css";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); //limpia error al iniciar el registro
    // Validación de campos
    if (!name || !email || !password || !confirmPassword) {
      setError("All fields are required.");
      return;
    }
    // Validación de passwords iguales
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    // Validación de pattern
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    // Validación de mayúsculas, minúsculas y números
    if (!hasUppercase || !hasLowercase || !hasNumbers) {
      setError(
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      );
      return;
    }
    //crea un usuario con email y password
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      //crea el documento del usuario en Firestore
      await setDoc(doc(db, "users", user.uid), {
        name: name,
        email: user.email,
        createdAt: new Date().toISOString(),
        favplaces: [],
      });

      navigate("/weather");
    } catch (err) {
      switch (err.code) {
        case "auth/email-already-in-use":
          setError("This email is already registered.");
          break;
        case "auth/invalid-email":
          setError("Invalid email format.");
          break;
        case "auth/weak-password":
          setError(
            "Password is too weak. It must be at least 8 characters long and include uppercase, lowercase, and numbers."
          );
          break;
        case "auth/operation-not-allowed":
          setError("Email/password registration is not enabled.");
          break;
        default:
          setError("Registration error. Please try again.");
          console.error(err);
      }
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Verificar si el usuario ya existe
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (!userDoc.exists()) {
        // Crear nuevo documento si no existe
        await setDoc(doc(db, "users", user.uid), {
          name: user.displayName,
          email: user.email,
          createdAt: new Date().toISOString(),
          favplaces: [],
        });
      }

      navigate("/weather");
    } catch (err) {
      setError("Error signing up with Google");
    }
  };

  const handleGithubSignUp = async () => {
    try {
      const provider = new GithubAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Verificar si el usuario ya existe
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (!userDoc.exists()) {
        // Crear nuevo documento si no existe
        await setDoc(doc(db, "users", user.uid), {
          name: user.displayName || "User",
          email: user.email,
          createdAt: new Date().toISOString(),
          favplaces: [],
        });
      }

      navigate("/weather");
    } catch (err) {
      setError("Error signing up with GitHub");
      console.error(err);
    }
  };

  return (
    <div className="register-container">
      <div className="register-box">
        <h1 className="register-title">Sign up for DaWeather</h1>
        <form onSubmit={handleRegister} className="register-form">
          <div className="input-group">
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="register-input"
              required
            />
          </div>
          <div className="input-group">
            <input
              type="email"
              placeholder="Mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="register-input"
              required
            />
          </div>
          <div className="input-group">
            <div style={{ position: "relative", width: "100%" }}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="register-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                }}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>
          <div className="input-group">
            <div style={{ position: "relative", width: "100%" }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="register-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                }}
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>
          <button type="submit" className="register-button">
            Register
          </button>
        </form>
        {error && <p className="error-message">{error}</p>}

        <button
          onClick={handleGoogleSignUp}
          className="google-signup-button"
          style={{
            marginTop: "1rem",
            marginBottom: "1rem",
            background: "#fff",
            color: "#757575",
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: "0.5rem 1.2rem",
            fontWeight: 600,
            fontSize: "1rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            width: "100%",
          }}
        >
          <img
            src="https://www.google.com/favicon.ico"
            alt="Google"
            style={{ width: "20px", height: "20px" }}
          />
          Sign up with Google
        </button>
        <button onClick={handleGithubSignUp} className="github-login-button">
          <img
            src="https://i.imgur.com/eYHjNMJ.png"
            alt="GitHub"
            style={{ width: "20px", height: "20px" }}
          />
          Continue with GitHub
        </button>

        <p className="register-link">
          Have an account? <a href="/login">Return to Login</a>
        </p>
      </div>
    </div>
  );
};

export default Register;
